/**
 * The portal's one API client.
 *
 * It is the generated client from `reference/deca-api/clients/typescript`, not a
 * parallel one: paths, params, bodies and responses are all checked against the
 * frozen `deca-openapi.yaml`, so a contract change surfaces here as a TypeScript
 * error rather than a runtime surprise.
 *
 * Every call is **same-origin `/api/v1/...`** — `baseUrl` stays empty. Vite
 * proxies `/api` in development; Caddy serves the portal and the API from one
 * origin in production. There is no CORS in the design and no hostname compiled
 * into the bundle, which matters because the production hostname is still
 * unconfirmed (architecture spec §23, item 3).
 */

import { createDecaClient, toApiError, type DecaClient } from '@deca/api'
import i18n from './i18n.ts'
import { getOrganisationContext } from './organisation.ts'
import { getAccessToken, clearSession } from './session.ts'

let onUnauthorizedHandler: () => void = () => clearSession()

/** Wired once at boot so a 401 anywhere routes back to the login screen. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorizedHandler = handler
}

export const api: DecaClient = createDecaClient({
  getToken: () => getAccessToken(),
  organisationId: () => getOrganisationContext()?.id ?? null,
  language: () => i18n.language,
  onUnauthorized: () => onUnauthorizedHandler(),
})

/**
 * Sent with every auth call. The server reads the refresh cookie only beside it, which with
 * `SameSite=Strict` is the CSRF defence, and beside it keeps the refresh token out of the body,
 * in the `HttpOnly` cookie alone.
 */
export const PORTAL_CLIENT_HEADER = { 'X-Requested-With': 'deca-portal' } as const

/**
 * Authenticated binary fetch — PDFs, QR images, the Excel template, the
 * import error report and a delivery confirmation's photographs. `openapi-fetch`
 * is built around JSON, so these go through plain `fetch` with the same
 * relative-URL and bearer-token conventions.
 *
 * Note what this is *not*: the BOE download. The QR encodes `/d/{token}`, which
 * must download with no login and no landing page (BOE Third.3–4), and the
 * portal must never wrap that URL in a screen of its own.
 *
 * The organisation context is applied here **separately** from the generated
 * client, because this path is plain `fetch` and shares none of its middleware.
 * Forgetting it would 404 a platform admin's PDF while the screen around it
 * answered for the company they had chosen.
 */
export async function fetchBinary(path: string, accept: string): Promise<Blob> {
  const token = getAccessToken()
  const organisation = getOrganisationContext()
  const response = await fetch(path, {
    headers: {
      Accept: accept,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(organisation ? { 'X-Organisation-Id': organisation.id } : {}),
      'Accept-Language': i18n.language,
    },
  })
  if (!response.ok) {
    // These routes answer failures with problem+json like every other one, so parse it and
    // throw the same `DecaApiError` the JSON client throws. `new Error('HTTP 404')` put the
    // status code in front of the user and lost the server's own localised message.
    let problem: unknown
    try {
      problem = await response.json()
    } catch {
      /* an error body that is not problem+json — `toApiError` still has the status */
    }
    throw toApiError(problem, response)
  }
  return response.blob()
}

/** Hands a blob to the browser's download machinery and cleans up after itself. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  // Revoking synchronously can race the download in Safari; one tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function downloadFile(path: string, accept: string, filename: string): Promise<void> {
  saveBlob(await fetchBinary(path, accept), filename)
}

export const MIME = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  png: 'image/png',
  // A delivery confirmation's photograph (#160).
  jpeg: 'image/jpeg',
} as const
